import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import VideoPlayer from '../components/Player/VideoPlayer';
import FavoriteButton from '../components/Common/FavoriteButton';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Play, Star, Calendar, ArrowLeft, Share2, Clock, User, Users, ThumbsUp, ChevronRight, Loader2, X } from 'lucide-react';

const MovieDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [movie, setMovie] = useState(null);
  const [movieData, setMovieData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(searchParams.get('play') === 'true');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedProgress, setSavedProgress] = useState(null);
  const [relatedMovies, setRelatedMovies] = useState([]);
  const [tmdbCast, setTmdbCast] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorMovies, setActorMovies] = useState([]);
  const [loadingActor, setLoadingActor] = useState(false);

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        setIsLoading(true);
        const [data, progress] = await Promise.all([
          xtreamApi.getVodInfo(id),
          xtreamApi.getItemProgress(id, 'movie').catch(() => ({}))
        ]);
        if (data) {
          setMovie(data.info || {});
          setMovieData(data.movie_data || {});

          // Fetch TMDB cast if tmdb_id available
          const tmdbId = data.info?.tmdb_id || data.movie_data?.tmdb_id;
          if (tmdbId) {
            xtreamApi.getTmdbCast(tmdbId).then(setTmdbCast).catch(() => {});
            xtreamApi.getSimilarMovies(tmdbId).then(setRelatedMovies).catch(() => {});
          }
        } else {
          setError('Movie not found');
        }
        if (progress && progress.current_time) {
          setSavedProgress(progress);
        }
      } catch (err) {
        console.error('Error fetching movie:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMovie();
  }, [id]);

  const handleProgress = useCallback((currentTime, duration) => {
    const posterImg = movie?.movie_image || movie?.cover_big || movieData?.stream_icon;
    const name = movie?.name || movieData?.name || 'Movie';
    xtreamApi.saveWatchProgress(id, 'movie', name, posterImg, currentTime, duration).catch(() => {});
  }, [id, movie, movieData]);

  const handleActorClick = async (actor) => {
    if (!actor.id) return;
    if (selectedActor?.id === actor.id) {
      setSelectedActor(null);
      setActorMovies([]);
      return;
    }
    setSelectedActor(actor);
    setLoadingActor(true);
    try {
      const movies = await xtreamApi.getActorMovies(actor.id);
      setActorMovies(movies.filter(m => String(m.id) !== String(movie?.tmdb_id)));
    } catch {
      setActorMovies([]);
    }
    setLoadingActor(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading movie..." />
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">{error || 'Movie not found'}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  if (isPlaying) {
    const container = movieData?.container_extension || 'mkv';
    const streamUrl = xtreamApi.getStreamUrl(id, 'movie', container);
    
    return (
      <div className="fixed inset-0 bg-black z-50">
        <VideoPlayer
          src={streamUrl}
          poster={movie.movie_image || movie.cover_big}
          title={movie.name || movieData?.name || 'Movie'}
          onBack={() => setIsPlaying(false)}
          isLive={false}
          onProgress={handleProgress}
          initialTime={savedProgress?.current_time || 0}
        />
      </div>
    );
  }

  const backdrop = movie.backdrop_path?.[0] || movie.movie_image || movie.cover_big;
  const poster = movie.movie_image || movie.cover_big;
  const title = movie.name || movieData?.name || 'Unknown';
  const rating = movie.rating || movieData?.rating || '0';
  const year = movie.releasedate || movie.year || '';
  const genre = movie.genre || '';
  const plot = movie.plot || movie.description || 'No description available';
  const director = movie.director || '';
  const cast = movie.cast || movie.actors || '';
  const duration = movie.duration || movieData?.duration || '';
  const progressPercent = savedProgress ? savedProgress.progress_percent : 0;

  const castList = cast ? cast.split(',').map(a => a.trim()).filter(Boolean) : [];
  const hasTmdbCast = tmdbCast.length > 0;

  return (
    <div data-testid="movie-detail-page" className="min-h-screen bg-[#0a0a0b]">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[500px]">
        <img src={backdrop} alt={title} className="w-full h-full object-cover"
          onError={(e) => { e.target.src = poster || 'https://via.placeholder.com/1920x1080?text=No+Image'; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />

        <button onClick={() => navigate(-1)}
          className="absolute top-20 left-4 sm:left-8 flex items-center gap-2 text-white/70 hover:text-white transition-colors z-10">
          <ArrowLeft className="w-5 h-5" /><span>Back</span>
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 lg:p-16">
          <div className="max-w-[1800px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="hidden lg:block w-64 flex-shrink-0">
                <img src={poster} alt={title} className="w-full rounded-xl shadow-2xl"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x450?text=No+Poster'; }} />
              </div>

              <div className="flex-1 space-y-4">
                <h1 className="text-3xl sm:text-5xl font-bold text-white">{title}</h1>

                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {rating && rating !== '0' && (
                    <span className="flex items-center gap-1 text-yellow-400">
                      <Star className="w-4 h-4 fill-yellow-400" />{parseFloat(rating).toFixed(1)}/10
                    </span>
                  )}
                  {year && (
                    <span className="flex items-center gap-1 text-gray-300"><Calendar className="w-4 h-4" />{year}</span>
                  )}
                  {duration && (
                    <span className="flex items-center gap-1 text-gray-300"><Clock className="w-4 h-4" />{duration}</span>
                  )}
                  {genre && (
                    <div className="flex gap-2 flex-wrap">
                      {genre.split(',').slice(0, 3).map((g, i) => (
                        <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-gray-300">{g.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-gray-300 text-base max-w-2xl leading-relaxed">{plot}</p>

                {director && (
                  <p className="text-sm text-gray-400 flex items-center gap-2">
                    <User className="w-4 h-4" /><span className="text-gray-500">Director:</span> {director}
                  </p>
                )}

                <div className="flex flex-wrap gap-4 pt-4">
                  <Button onClick={() => setIsPlaying(true)}
                    data-testid="movie-play-btn"
                    className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-base font-semibold">
                    <Play className="w-5 h-5 mr-2 fill-black" />
                    {progressPercent > 0 ? 'Resume' : 'Play'}
                  </Button>
                  {progressPercent > 0 && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <div className="w-24 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div style={{backgroundColor:"var(--accent)"}} className="h-full rounded-full" style={{ width: `${progressPercent}%` }} />
                      </div>
                      <span>{Math.round(progressPercent)}%</span>
                    </div>
                  )}
                  <FavoriteButton
                    contentId={id}
                    contentType="movie"
                    name={title}
                    poster={poster}
                    rating={rating}
                    size="lg"
                    className="border border-gray-500 text-white hover:bg-white/10 px-6 py-3 rounded-md flex items-center gap-2"
                  />
                  <Button variant="ghost" className="text-white hover:bg-white/10 px-6 py-6">
                    <Share2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Below-the-fold content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-16">

        {/* Cast / Actors Section */}
        {(hasTmdbCast || castList.length > 0) && (
          <section data-testid="movie-cast-section" className="py-8 border-b border-gray-800/50">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-5">
              <span style={{backgroundColor:"var(--accent)"}} className="w-1 h-5 rounded-full" />
              Actors
            </h2>
            {hasTmdbCast ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tmdbCast.map((actor, i) => (
                  <div key={actor.id || i} data-testid={`cast-member-${i}`}
                    onClick={() => handleActorClick(actor)}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                      selectedActor?.id === actor.id
                        ? ''
                        : 'bg-[#141417] hover:bg-[#1a1a1d]'
                    }`}
                    style={selectedActor?.id === actor.id ? {backgroundColor:'var(--accent-bg)',boxShadow:'inset 0 0 0 1px var(--accent)'} : {}}>
                    <div className="w-14 h-14 rounded-full bg-gray-800 flex-shrink-0 overflow-hidden">
                      {actor.image ? (
                        <img src={actor.image} alt={actor.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`w-full h-full items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800 ${actor.image ? 'hidden' : 'flex'}`}>
                        <span className="text-xl font-semibold text-gray-400">{actor.name.charAt(0)}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{actor.name}</p>
                      {actor.character && (
                        <p className="text-gray-500 text-xs truncate">{actor.character}</p>
                      )}
                    </div>
                    <ChevronRight className={`w-4 h-4 text-gray-600 flex-shrink-0 transition-transform ${selectedActor?.id === actor.id ? 'rotate-90' : ''}`} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                {castList.map((actor, i) => (
                  <div key={i} data-testid={`cast-member-${i}`}
                    className="flex-shrink-0 w-28 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center mb-2">
                      <span className="text-xl font-semibold text-gray-400">{actor.charAt(0)}</span>
                    </div>
                    <p className="text-gray-300 text-xs font-medium line-clamp-2 leading-tight">{actor}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Actor filmography panel */}
            {selectedActor && (
              <div data-testid="actor-filmography" className="mt-4 p-4 bg-[#111114] rounded-xl border border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    {selectedActor.image && (
                      <img src={selectedActor.image} alt="" className="w-8 h-8 rounded-full object-cover" />
                    )}
                    More from {selectedActor.name}
                  </h3>
                  <button onClick={() => { setSelectedActor(null); setActorMovies([]); }}
                    className="text-gray-500 hover:text-white p-1"><X className="w-4 h-4" /></button>
                </div>
                {loadingActor ? (
                  <div className="flex justify-center py-6">
                    <Loader2 style={{color:"var(--accent)"}} className="w-6 h-6 animate-spin" />
                  </div>
                ) : actorMovies.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No other movies found</p>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {actorMovies.map(m => (
                      <Link key={m.id} to={`/movie/${m.id}`} data-testid={`actor-movie-${m.id}`}
                        className="flex-shrink-0 group">
                        <div className="relative w-28 rounded-lg overflow-hidden">
                          <div className="aspect-[2/3] bg-gray-800">
                            {m.poster ? (
                              <img src={m.poster} alt={m.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs p-2 text-center">{m.title}</div>
                            )}
                          </div>
                          {m.rating > 0 && (
                            <div className="absolute top-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/70 rounded text-[10px]">
                              <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
                              <span className="text-white">{m.rating}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-300 text-[11px] mt-1.5 line-clamp-1 w-28">{m.title}</p>
                        <p className="text-gray-600 text-[10px] line-clamp-1 w-28">{m.character}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* You May Also Like */}
        {relatedMovies.length > 0 && (
          <section data-testid="movie-related-section" className="py-8">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-5">
              <ThumbsUp style={{color:"var(--accent)"}} className="w-5 h-5" />
              You May Also Like
            </h2>
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
              {relatedMovies.map((m) => (
                <Link
                  key={m.id}
                  to={`/search?q=${encodeURIComponent(m.title)}`}
                  data-testid={`related-movie-${m.id}`}
                  className="flex-shrink-0 group"
                >
                  <div className="relative w-36 rounded-lg overflow-hidden">
                    <div className="aspect-[2/3] bg-gray-800">
                      {m.poster ? (
                        <img
                          src={m.poster}
                          alt={m.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs p-2 text-center">{m.title}</div>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                        </div>
                      </div>
                    </div>
                    {m.rating > 0 && (
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px]">
                        <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
                        <span className="text-white">{m.rating}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-300 text-xs font-medium mt-2 line-clamp-1 w-36">{m.title}</p>
                  {m.year && <p className="text-gray-600 text-[10px] w-36">{m.year}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MovieDetailPage;
